export type Phase = 'playing' | 'rebuilding';
export class Lifecycle {
  epoch = 1;
  phase: Phase = 'playing';
  elapsed = 0;
  begin(): number {
    this.epoch++;
    this.phase = 'rebuilding';
    this.elapsed = 0;
    return this.epoch;
  }
  valid(epoch: number): boolean {
    return this.epoch === epoch;
  }
  finish(): void {
    this.phase = 'playing';
    this.elapsed = 0;
  }
}
export class JobQueue {
  private jobs: { epoch: number; run: () => void; queuedAt: number }[] = [];
  rejected = 0;
  completed = 0;
  longestJobMs = 0;
  constructor(
    private life: Lifecycle,
    readonly limit = 256,
  ) {}
  push(run: () => void): boolean {
    if (this.jobs.length >= this.limit) {
      this.rejected++;
      return false;
    }
    this.jobs.push({ epoch: this.life.epoch, run, queuedAt: performance.now() });
    return true;
  }
  drain(max = 2, budgetMs = 4): number {
    const start = performance.now();
    let count = 0;
    while (this.jobs.length && count < max && performance.now() - start < budgetMs) {
      const job = this.jobs.shift()!;
      if (this.life.valid(job.epoch)) {
        const started = performance.now();
        job.run();
        this.longestJobMs = Math.max(this.longestJobMs, performance.now() - started);
        this.completed++;
        count++;
      }
    }
    return count;
  }
  clear(): void {
    this.jobs.length = 0;
    this.rejected = this.completed = this.longestJobMs = 0;
  }
  get size(): number {
    return this.jobs.length;
  }
  get oldestMs(): number {
    return this.jobs.length ? performance.now() - this.jobs[0].queuedAt : 0;
  }
}
export class FixedClock {
  accumulator = 0;
  lastSteps = 0;
  maxSteps = 0;
  totalSteps = 0;
  discardedSeconds = 0;
  advance(dt: number, step: () => void): number {
    this.discardedSeconds += Math.max(0, dt - 0.25);
    this.accumulator += Math.min(dt, 0.25);
    let count = 0;
    while (this.accumulator >= 1 / 60 && count < 4) {
      step();
      this.accumulator -= 1 / 60;
      count++;
    }
    if (count === 4) {
      this.discardedSeconds += Math.max(0, this.accumulator - 0.25);
      this.accumulator = Math.min(this.accumulator, 0.25);
    }
    this.totalSteps += count;
    this.lastSteps = count;
    this.maxSteps = Math.max(this.maxSteps, count);
    return Math.min(1, this.accumulator * 60);
  }
  clear(): void {
    this.accumulator = 0;
  }
}
